import { useEffect, useState } from "react";
import { Badge, Card, CardBody, Spinner, Table } from "reactstrap";
import { useWorkOrderOverview } from "../WorkOrderOverviewContext";
import { formatCurrency } from "utils/currency";
import { getWorkOrderSubcontracts } from "features/WorkOrders/services/WorkOrderSubcontractService";
import type { WorkOrderSubcontract } from "features/WorkOrders/types/work-order.types";

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ASSIGNED: { label: "Assigned", color: "info" },
  IN_PROGRESS: { label: "In Progress", color: "warning" },
  COMPLETED: { label: "Completed", color: "success" },
  CANCELLED: { label: "Cancelled", color: "secondary" },
};

export default function Subcontracts() {
  const { workOrder } = useWorkOrderOverview();
  const [subcontracts, setSubcontracts] = useState<WorkOrderSubcontract[]>([]);
  const [loading, setLoading] = useState(false);

  const agreedTotal = subcontracts.reduce(
    (sum, sc) => sum + Number(sc.agreedAmount ?? 0),
    0,
  );
  const finalTotal = subcontracts.reduce(
    (sum, sc) => sum + Number(sc.finalAmount ?? 0),
    0,
  );

  useEffect(() => {
    if (!workOrder?.id) return;
    setLoading(true);
    getWorkOrderSubcontracts(workOrder.id)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setSubcontracts(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workOrder?.id]);

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h6 className="text-uppercase mb-0">Subcontracts</h6>
          {subcontracts.length > 0 && (
            <div className="d-flex gap-3 small text-muted">
              <span>
                Agreed:{" "}
                <span className="fw-semibold text-body">
                  {formatCurrency(agreedTotal)}
                </span>
              </span>
              {finalTotal > 0 && (
                <span>
                  Final:{" "}
                  <span className="fw-semibold text-body">
                    {formatCurrency(finalTotal)}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-3">
            <Spinner size="sm" />
          </div>
        ) : subcontracts.length === 0 ? (
          <p className="text-muted mb-0">No subcontracts for this work order.</p>
        ) : (
          <div className="table-responsive table-card">
            <Table className="table-borderless align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Subcontractor</th>
                  <th>Description</th>
                  <th className="text-end">Agreed</th>
                  <th className="text-end">Final</th>
                  <th className="text-center">Labour</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {subcontracts.map((sc, idx) => {
                  const { label, color } = STATUS_MAP[sc.status] ?? {
                    label: sc.status,
                    color: "light",
                  };
                  return (
                    <tr key={sc.id}>
                      <td className="text-muted">{idx + 1}</td>
                      <td>
                        <span className="fw-medium">{sc.supplier.name}</span>
                        {sc.supplier.phone && (
                          <div className="text-muted small">
                            {sc.supplier.phone}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: 220 }}
                          title={sc.description}
                        >
                          {sc.description}
                        </span>
                      </td>
                      <td className="text-end fw-semibold">
                        {formatCurrency(sc.agreedAmount)}
                      </td>
                      <td className="text-end">
                        {formatCurrency(sc.finalAmount)}
                      </td>
                      <td className="text-center">{sc.labourCount ?? "—"}</td>
                      <td>
                        <Badge color={color}>{label}</Badge>
                      </td>
                      <td
                        className="text-muted"
                        style={{ whiteSpace: "pre-wrap", maxWidth: 200 }}
                      >
                        {sc.notes?.trim() || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan={3} className="text-end fw-semibold small text-muted">
                    Total
                  </td>
                  <td className="text-end fw-bold tabular-nums">
                    {formatCurrency(agreedTotal)}
                  </td>
                  <td className="text-end fw-bold tabular-nums">
                    {finalTotal > 0 ? formatCurrency(finalTotal) : "—"}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
